import {
  createContext,
  type FormEvent,
  type ReactNode,
  useCallback,
  useContext,
  useRef,
  useState
} from 'react';

import to from 'await-to-js';
import PropTypes from 'prop-types';

import { Button } from '@/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/shadcn/dialog';
import { Input } from '@/shadcn/input';
import { Label } from '@/shadcn/label';

import { reAuthConduent } from '@/client/account';
import { notify } from '@/components/toast';
import { SpanType, withSpanAsync } from '@/tracing';

type ReAuthContextType = {
  requestSiebelReAuth: () => Promise<boolean>;
};

const DEFAULT_CONTEXT: ReAuthContextType = {
  requestSiebelReAuth: async () => false
};

const ReAuthContext = createContext<ReAuthContextType>(DEFAULT_CONTEXT);
ReAuthContext.displayName = 'ReAuthContext';

export const ReAuthProvider = ({ children }: { children: ReactNode }) => {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const resolversRef = useRef<Array<(success: boolean) => void>>([]);

  const settle = useCallback((success: boolean) => {
    const resolvers = resolversRef.current;
    resolversRef.current = [];
    setOpen(false);
    setPassword('');
    setLoading(false);
    resolvers.forEach(resolve => resolve(success));
  }, []);

  const requestSiebelReAuth = useCallback(
    () =>
      new Promise<boolean>(resolve => {
        resolversRef.current.push(resolve);
        setOpen(true);
      }),
    []
  );

  const handleSubmit = (event?: FormEvent) =>
    withSpanAsync('siebelReAuth', SpanType.processing, async () => {
      if (event) event.preventDefault();
      if (loading || !password) return;

      setLoading(true);
      const [err, response] = await to(reAuthConduent(password));

      // withSpanAsync resolves with the error instead of rejecting, so a
      // failed re-auth also surfaces as a resolved Error value.
      if (err || response instanceof Error) {
        setLoading(false);
        notify.error(
          'Siebel re-authentication failed. Please check your password and try again.'
        );
        // Keep the modal open so the user can retry.
        return;
      }

      notify.success('Reconnected to Siebel');
      settle(true);
    });

  return (
    <ReAuthContext.Provider value={{ requestSiebelReAuth }}>
      <Dialog
        onOpenChange={isOpen => {
          if (!isOpen) settle(false);
        }}
        open={open}
      >
        <DialogContent>
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle>Siebel session expired</DialogTitle>
              <DialogDescription>
                Your Siebel session has expired. Re-enter your Siebel password
                to continue — your action will be submitted automatically.
              </DialogDescription>
            </DialogHeader>

            <div className="flex flex-col gap-4 py-4">
              <div className="flex flex-col gap-2">
                <Label htmlFor="siebel-password">Siebel Password</Label>
                <Input
                  autoComplete="current-password"
                  autoFocus
                  data-testid="siebel-reauth-password"
                  id="siebel-password"
                  onChange={event => setPassword(event.target.value)}
                  type="password"
                  value={password}
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                onClick={() => settle(false)}
                type="button"
                variant="outline"
              >
                Cancel
              </Button>
              <Button
                data-testid="siebel-reauth-submit"
                disabled={!password || loading}
                type="submit"
              >
                {loading ? 'Reconnecting…' : 'Reconnect'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {children}
    </ReAuthContext.Provider>
  );
};

ReAuthProvider.propTypes = {
  children: PropTypes.node.isRequired
};

/**
 * Hook to access the Siebel re-authentication context.
 */
export const useReAuth = () => useContext(ReAuthContext);
